import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Crown, LogOut, User, Bell, TrendingUp, DollarSign, BarChart3, Plus,
  PieChart, Globe, Briefcase, FileText, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

const GovernmentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [bonds, setBonds] = useState([]);
  const [swfPortfolio, setSwfPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createBondOpen, setCreateBondOpen] = useState(false);
  const [bondData, setBondData] = useState({
    name: '',
    description: '',
    face_value: '',
    coupon_rate: '',
    maturity_years: '',
    total_units: ''
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bondsRes, swfRes] = await Promise.all([
        axios.get(`${API}/bonds`, { headers, withCredentials: true }),
        axios.get(`${API}/swf/portfolio`, { headers, withCredentials: true })
      ]);
      setBonds(bondsRes.data);
      setSwfPortfolio(swfRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBond = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bonds`, {
        name: bondData.name,
        description: bondData.description,
        face_value: parseFloat(bondData.face_value),
        coupon_rate: parseFloat(bondData.coupon_rate),
        maturity_years: parseInt(bondData.maturity_years),
        total_units: parseInt(bondData.total_units)
      }, { headers, withCredentials: true });
      
      toast.success('Bond issued successfully!');
      setCreateBondOpen(false);
      setBondData({ name: '', description: '', face_value: '', coupon_rate: '', maturity_years: '', total_units: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue bond');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const COLORS = ['#0047AB', '#10B981', '#D4AF37', '#8B5CF6', '#EC4899'];

  // Mock treasury data
  const treasuryData = {
    cash_balance: 125.8,
    short_term_debt: 45.2,
    long_term_debt: 892.5,
    total_revenue_ytd: 485.3,
    total_expenditure_ytd: 412.8
  };

  const performanceData = [
    { month: 'Jul', value: 850 },
    { month: 'Aug', value: 870 },
    { month: 'Sep', value: 865 },
    { month: 'Oct', value: 880 },
    { month: 'Nov', value: 875 },
    { month: 'Dec', value: 892 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="government-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary hidden sm:block">Royal CBDC</span>
              <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded font-mono uppercase tracking-wider hidden md:block">
                Government Portal
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/ai-monitoring">
                <Button variant="outline" size="sm" className="gap-2 hidden md:flex">
                  <BarChart3 className="h-4 w-4" /> AI Monitoring
                </Button>
              </Link>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Crown className="h-4 w-4 text-amber-600" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-normal text-primary">
              Treasury & Sovereign Finance
            </h1>
            <p className="text-muted-foreground mt-1">Manage bonds, treasury operations, and sovereign wealth</p>
          </div>
          <Dialog open={createBondOpen} onOpenChange={setCreateBondOpen}>
            <DialogTrigger asChild>
              <Button className="mt-4 md:mt-0 gap-2" data-testid="issue-bond-btn">
                <Plus className="h-4 w-4" /> Issue New Bond
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Issue Sovereign Bond</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBond} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Bond Name</Label>
                  <Input
                    value={bondData.name}
                    onChange={(e) => setBondData({ ...bondData, name: e.target.value })}
                    placeholder="e.g., Treasury Bond 2029"
                    required
                    data-testid="bond-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={bondData.description}
                    onChange={(e) => setBondData({ ...bondData, description: e.target.value })}
                    rows={2}
                    required
                    data-testid="bond-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Face Value (USD)</Label>
                    <Input
                      type="number"
                      value={bondData.face_value}
                      onChange={(e) => setBondData({ ...bondData, face_value: e.target.value })}
                      required
                      data-testid="bond-face-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Coupon Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={bondData.coupon_rate}
                      onChange={(e) => setBondData({ ...bondData, coupon_rate: e.target.value })}
                      required
                      data-testid="bond-coupon-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Maturity (Years)</Label>
                    <Input
                      type="number"
                      value={bondData.maturity_years}
                      onChange={(e) => setBondData({ ...bondData, maturity_years: e.target.value })}
                      required
                      data-testid="bond-maturity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Units</Label>
                    <Input
                      type="number"
                      value={bondData.total_units}
                      onChange={(e) => setBondData({ ...bondData, total_units: e.target.value })}
                      required
                      data-testid="bond-units-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="bond-submit-btn">
                  Issue Bond
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="treasury" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="treasury" data-testid="tab-treasury">Treasury</TabsTrigger>
            <TabsTrigger value="bonds" data-testid="tab-bonds">Sovereign Bonds</TabsTrigger>
            <TabsTrigger value="swf" data-testid="tab-swf">Wealth Fund</TabsTrigger>
          </TabsList>

          {/* Treasury Tab */}
          <TabsContent value="treasury" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Cash Balance', value: `$${treasuryData.cash_balance}B`, icon: <DollarSign className="h-5 w-5 text-green-500" /> },
                { label: 'Short-term Debt', value: `$${treasuryData.short_term_debt}B`, icon: <TrendingUp className="h-5 w-5 text-blue-500" /> },
                { label: 'Long-term Debt', value: `$${treasuryData.long_term_debt}B`, icon: <Briefcase className="h-5 w-5 text-purple-500" /> },
                { label: 'Revenue YTD', value: `$${treasuryData.total_revenue_ytd}B`, icon: <ArrowUpRight className="h-5 w-5 text-green-500" /> },
                { label: 'Expenditure YTD', value: `$${treasuryData.total_expenditure_ytd}B`, icon: <ArrowDownRight className="h-5 w-5 text-red-500" /> }
              ].map((stat, idx) => (
                <Card key={idx} className="dashboard-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">{stat.label}</span>
                      {stat.icon}
                    </div>
                    <p className="font-mono text-xl font-semibold" data-testid={`treasury-stat-${idx}`}>{stat.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Fiscal Balance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0047AB" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#0047AB" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#0047AB" fill="url(#colorValue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bonds Tab */}
          <TabsContent value="bonds">
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Active Sovereign Bonds</CardTitle>
                <CardDescription>Government debt instruments available for subscription</CardDescription>
              </CardHeader>
              <CardContent>
                {bonds.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No bonds issued</p>
                    <p className="text-sm mt-1">Issue your first sovereign bond</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bonds.map((bond, idx) => (
                      <div key={idx} className="p-4 rounded-lg border border-border" data-testid={`bond-item-${idx}`}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold">{bond.name}</h3>
                            <p className="text-sm text-muted-foreground">{bond.description}</p>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <p className="font-mono text-lg font-semibold">${bond.face_value}</p>
                              <p className="text-xs text-muted-foreground">Face Value</p>
                            </div>
                            <div className="text-center">
                              <p className="font-mono text-lg font-semibold text-green-600">{bond.coupon_rate}%</p>
                              <p className="text-xs text-muted-foreground">Coupon</p>
                            </div>
                            <div className="text-center">
                              <p className="font-mono text-lg font-semibold">{bond.maturity_years}Y</p>
                              <p className="text-xs text-muted-foreground">Maturity</p>
                            </div>
                            <div className="text-center">
                              <p className="font-mono text-lg font-semibold">{bond.available_units.toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">Available</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sovereign Wealth Fund Tab */}
          <TabsContent value="swf" className="space-y-6">
            {swfPortfolio && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="md:col-span-2 sovereign-card bg-primary text-primary-foreground">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <p className="text-primary-foreground/70 text-sm uppercase tracking-wider mb-1">Total Assets Under Management</p>
                          <p className="font-mono text-4xl md:text-5xl font-semibold" data-testid="swf-total">
                            ${swfPortfolio.total_assets}B
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                          <PieChart className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4 mt-8">
                        {Object.entries(swfPortfolio.performance).map(([key, value], idx) => (
                          <div key={idx} className="text-center">
                            <p className={`font-mono text-xl font-semibold ${value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {value}%
                            </p>
                            <p className="text-xs text-primary-foreground/60 uppercase">{key.replace('_', ' ')}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="sovereign-card">
                    <CardHeader>
                      <CardTitle className="font-sans text-lg">Asset Allocation</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={swfPortfolio.allocations}
                              dataKey="allocation"
                              nameKey="asset_class"
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                            >
                              {swfPortfolio.allocations.map((entry, idx) => (
                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="sovereign-card">
                  <CardHeader>
                    <CardTitle className="font-sans text-lg">Portfolio Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 text-sm font-medium text-muted-foreground">Asset Class</th>
                            <th className="text-right py-3 text-sm font-medium text-muted-foreground">Allocation</th>
                            <th className="text-right py-3 text-sm font-medium text-muted-foreground">Value (USD)</th>
                            <th className="text-right py-3 text-sm font-medium text-muted-foreground">YTD Return</th>
                          </tr>
                        </thead>
                        <tbody>
                          {swfPortfolio.allocations.map((asset, idx) => (
                            <tr key={idx} className="border-b border-border/50" data-testid={`swf-allocation-${idx}`}>
                              <td className="py-3 font-medium">{asset.asset_class}</td>
                              <td className="py-3 text-right font-mono">{asset.allocation}%</td>
                              <td className="py-3 text-right font-mono">${asset.value}B</td>
                              <td className={`py-3 text-right font-mono ${asset.return_ytd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {asset.return_ytd >= 0 ? '+' : ''}{asset.return_ytd}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                <Card className="sovereign-card">
                  <CardHeader>
                    <CardTitle className="font-sans text-lg">Geographic Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {swfPortfolio.geographic_distribution.map((region, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-muted/30 text-center" data-testid={`swf-region-${idx}`}>
                          <Globe className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                          <p className="font-mono text-2xl font-semibold">{region.percentage}%</p>
                          <p className="text-sm text-muted-foreground">{region.region}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default GovernmentDashboard;
